/**
 * Export stylesheet builder (PDF / print).
 *
 * Produces the full CSS injected before a Paged.js run so the export is:
 *  - forced into a clean LIGHT "Formal-Federal" theme (re-pins --bw-* tokens to
 *    the spec palette, neutralizes dark-mode utilities the renderers use),
 *  - typeset in Times New Roman 12pt with navy headings and real tables/lists,
 *  - paginated with running header (company | solicitation), footer
 *    (document title · section · Page X of Y), and a furniture-free cover page.
 *
 * Page numbers (counter(page)/counter(pages)) and the section named-string
 * (string(sectiontitle)) require a CSS Paged Media engine — which is exactly why
 * the PDF path uses Paged.js rather than raw window.print().
 */

// Spec design tokens (BidWin_Export_Styling_Spec.md)
export const FED = {
  navy: '#1F4E96',
  red: '#C8102E',
  ink: '#1A1A1A',
  muted: '#5A6472',
  slate: '#33415C',
  border: '#C9D3E0',
  zebra: '#F2F5FA',
  headerFill: '#1F4E96',
};

/** CSS-escape a string for use inside a `content: "..."` declaration. */
function cssString(value: string): string {
  return `"${(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

export interface ExportCssOptions {
  companyName: string;
  solicitation: string;
  documentTitle: string; // footer-left (document / volume title)
}

export function buildExportCss(opts: ExportCssOptions): string {
  const headerLeft = opts.companyName ? cssString(opts.companyName) : '""';
  const headerRight = opts.solicitation
    ? cssString(`Solicitation ${opts.solicitation}`)
    : '""';
  const footerLeft = opts.documentTitle ? cssString(opts.documentTitle) : '""';

  return `
/* ===== Forced light Formal-Federal theme ===== */
.export-print-theme {
  --bw-app: 255 255 255;
  --bw-surface: 255 255 255;
  --bw-surface-subtle: 242 245 250;
  --bw-border: 201 211 224;
  --bw-ink: 26 26 26;
  --bw-muted: 90 100 114;
  --bw-primary: 31 78 150;
  --bw-primary-tint: 240 245 252;
  --bw-accent: 200 16 46;

  --bw-fit-strong: 21 114 47; --bw-fit-strong-bg: 230 245 236; --bw-fit-strong-border: 183 223 196; --bw-fit-strong-stroke: 27 135 63;
  --bw-fit-moderate: 138 90 0; --bw-fit-moderate-bg: 255 248 236; --bw-fit-moderate-border: 245 213 138; --bw-fit-moderate-stroke: 217 154 43;
  --bw-fit-weak: 58 70 89; --bw-fit-weak-bg: 237 241 247; --bw-fit-weak-border: 194 205 220; --bw-fit-weak-stroke: 194 205 220;
  --bw-track: 237 241 247;
  --bw-ev-grounded: 31 78 150; --bw-ev-grounded-bg: 240 245 252; --bw-ev-grounded-border: 31 78 150;
  --bw-ev-inferred: 138 90 0; --bw-ev-inferred-bg: 255 248 236; --bw-ev-inferred-border: 217 154 43;
  --bw-ev-gap: 90 100 114; --bw-ev-gap-bg: 247 249 252; --bw-ev-gap-border: 194 205 220;
  --bw-urgent: 200 30 30; --bw-urgent-bg: 255 234 234; --bw-urgent-border: 245 170 170;
  --bw-success: 21 114 47; --bw-success-bg: 230 245 236; --bw-success-border: 183 223 196;

  color-scheme: light;
  background: #ffffff;
  color: ${FED.ink};
  font-family: "Times New Roman", Georgia, serif;
  font-size: 12pt;
  line-height: 1.3;
}

/* Neutralize the specific dark: utilities the renderers hard-code */
.export-print-theme [class*="dark:bg-gray-9"],
.export-print-theme [class*="dark:bg-gray-8"] { background-color: #ffffff !important; }
.export-print-theme [class*="dark:bg-gray-800/50"] { background-color: ${FED.zebra} !important; }
.export-print-theme [class*="dark:text-white"],
.export-print-theme [class*="dark:text-gray-3"],
.export-print-theme [class*="dark:text-gray-2"] { color: ${FED.ink} !important; }
.export-print-theme [class*="dark:border-gray-7"] { border-color: ${FED.border} !important; }
.export-print-theme [class*="dark:bg-blue-9"] { background-color: ${FED.zebra} !important; }
.export-print-theme [class*="dark:bg-amber-9"] { background-color: #fff8ec !important; }

/* ===== Typography (Formal-Federal) ===== */
.export-print-theme p { color: ${FED.ink}; text-align: left; margin: 0 0 8pt; font-size: 12pt; }
/* Section E: body prose must be >= 12pt. Bump small body text (tables/captions
   keep their own sizes below; .text-xs stays for badges/captions). */
.export-print-theme li, .export-print-theme .text-sm, .export-print-theme .text-base { font-size: 12pt; }
.export-print-theme h1 {
  font-family: "Times New Roman", Georgia, serif;
  font-size: 15pt; font-weight: 700; color: ${FED.navy};
  border-bottom: 1.25pt solid ${FED.navy};
  padding-bottom: 4pt; margin: 12pt 0 6pt;
  string-set: sectiontitle content(text);
  break-after: avoid;
}
.export-print-theme h2 { font-size: 13pt; font-weight: 700; color: ${FED.navy}; margin: 12pt 0 4pt; break-after: avoid; }
.export-print-theme h3 { font-size: 12pt; font-weight: 700; color: ${FED.slate}; margin: 10pt 0 3pt; break-after: avoid; }
.export-print-theme h4 { font-size: 12pt; font-weight: 700; color: ${FED.slate}; margin: 8pt 0 3pt; }

.export-print-theme ul, .export-print-theme ol { margin: 0 0 8pt 0; padding-left: 22pt; }
.export-print-theme li { margin: 0 0 3pt; }
.export-print-theme ul li::marker { color: ${FED.navy}; }

/* ===== Tables (navy header, zebra, light borders) ===== */
.export-print-theme table { width: 100%; border-collapse: collapse; margin: 6pt 0 10pt; }
.export-print-theme th, .export-print-theme td {
  border: 0.75pt solid ${FED.border};
  padding: 6pt 9pt; text-align: left; vertical-align: top; font-size: 11pt;
}
.export-print-theme thead th, .export-print-theme th {
  background-color: ${FED.headerFill} !important;
  color: #ffffff !important; font-weight: 700; font-size: 10pt;
}
.export-print-theme tbody tr:nth-child(even) { background-color: ${FED.zebra} !important; }

/* Keep blocks from splitting awkwardly */
.export-print-theme table, .export-print-theme tr, .export-print-theme td, .export-print-theme th,
.export-print-theme li, .export-print-theme .rounded-lg, .export-print-theme .rounded-xl,
.export-print-theme .no-break { break-inside: avoid; }
.export-print-theme thead { display: table-header-group; }

/* Hide interactive chrome */
.export-print-theme button, .export-print-theme .no-print { display: none !important; }

/* The cover page already paints itself; ensure it owns page 1 */
.export-cover-page { break-after: page; }
/* The cover's identity meta block is a borderless label-value list, NOT a data
   table — neutralize the body table grid/zebra and the body H1 underline so the
   PDF cover matches the Word cover and the reference. */
.export-print-theme .export-cover-page table,
.export-print-theme .export-cover-page tr,
.export-print-theme .export-cover-page td,
.export-print-theme .export-cover-page th {
  border: none !important;
  background-color: transparent !important;
  font-size: inherit;
}
.export-print-theme .export-cover-page h1 { border-bottom: none !important; padding-bottom: 0 !important; }

/* ===== Paged.js page furniture ===== */
@page {
  size: letter;
  margin: 1in;

  @top-left {
    content: ${headerLeft};
    font: 700 9pt "Times New Roman", serif; color: ${FED.navy};
    vertical-align: bottom; padding-bottom: 4pt;
    border-bottom: 0.75pt solid ${FED.navy};
  }
  @top-center { content: ""; border-bottom: 0.75pt solid ${FED.navy}; }
  @top-right {
    content: ${headerRight};
    font: 9pt "Times New Roman", serif; color: ${FED.muted};
    vertical-align: bottom; padding-bottom: 4pt;
    border-bottom: 0.75pt solid ${FED.navy};
  }

  @bottom-left {
    content: ${footerLeft};
    font: 8pt "Times New Roman", serif; color: ${FED.muted};
    vertical-align: top; padding-top: 4pt;
    border-top: 0.75pt solid ${FED.navy};
  }
  @bottom-center {
    content: string(sectiontitle);
    font: 8pt "Times New Roman", serif; color: ${FED.muted};
    vertical-align: top; padding-top: 4pt;
    border-top: 0.75pt solid ${FED.navy};
  }
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font: 8pt "Times New Roman", serif; color: ${FED.muted};
    vertical-align: top; padding-top: 4pt;
    border-top: 0.75pt solid ${FED.navy};
  }
}

/* Cover page: no running header/footer */
@page :first {
  @top-left { content: ""; border-bottom: none; }
  @top-center { content: ""; border-bottom: none; }
  @top-right { content: ""; border-bottom: none; }
  @bottom-left { content: ""; border-top: none; }
  @bottom-center { content: ""; border-top: none; }
  @bottom-right { content: ""; border-top: none; }
}

/* ===== Landscape pages (wide pricing tables) ===== */
/* The CLIN schedule / labor-rate buildup tables run 7-10 columns wide —
   too wide for a 6.5in portrait page. Sections flagged with this class get
   their own named landscape page(s); Paged.js forces a page break whenever
   the named page changes, so the section starts fresh in landscape and any
   section after it returns to the default portrait page. */
.export-print-theme .export-landscape-section { page: landscapepricing; }
@page landscapepricing {
  size: letter landscape;
  margin: 0.75in;

  @top-left {
    content: ${headerLeft};
    font: 700 9pt "Times New Roman", serif; color: ${FED.navy};
    vertical-align: bottom; padding-bottom: 4pt;
    border-bottom: 0.75pt solid ${FED.navy};
  }
  @top-center { content: ""; border-bottom: 0.75pt solid ${FED.navy}; }
  @top-right {
    content: ${headerRight};
    font: 9pt "Times New Roman", serif; color: ${FED.muted};
    vertical-align: bottom; padding-bottom: 4pt;
    border-bottom: 0.75pt solid ${FED.navy};
  }

  @bottom-left {
    content: ${footerLeft};
    font: 8pt "Times New Roman", serif; color: ${FED.muted};
    vertical-align: top; padding-top: 4pt;
    border-top: 0.75pt solid ${FED.navy};
  }
  @bottom-center {
    content: string(sectiontitle);
    font: 8pt "Times New Roman", serif; color: ${FED.muted};
    vertical-align: top; padding-top: 4pt;
    border-top: 0.75pt solid ${FED.navy};
  }
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font: 8pt "Times New Roman", serif; color: ${FED.muted};
    vertical-align: top; padding-top: 4pt;
    border-top: 0.75pt solid ${FED.navy};
  }
}
`;
}
